/**
 * ObservationForm — inline form to create a survey observation.
 *
 * @remarks
 * Pre-fills position from the current capture's camera coords.
 * Calls onCreated when the API call succeeds; onCancel to dismiss.
 */

import { useState } from 'react';
import {
  OBSERVATION_TYPE_LABELS,
  type CreateObservationPayload,
  type ObservationObjectType,
} from '../types/survey.types';

interface ObservationFormProps {
  sessionId: string;
  captureId: string;
  defaultLat: number;
  defaultLon: number;
  onCreated: (payload: CreateObservationPayload) => Promise<void>;
  onCancel: () => void;
}

export function ObservationForm({
  sessionId,
  captureId,
  defaultLat,
  defaultLon,
  onCreated,
  onCancel,
}: ObservationFormProps): JSX.Element {
  const [description, setDescription] = useState('');
  const [objectType, setObjectType] = useState<ObservationObjectType | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!description.trim()) return;

    setSaving(true);
    setError(null);
    try {
      await onCreated({
        sessionId,
        captureId,
        description: description.trim(),
        objectType: objectType !== '' ? objectType : undefined,
        obsLat: defaultLat,
        obsLon: defaultLon,
      });
      setDescription('');
      setObjectType('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className="sv-obs-form"
      onSubmit={e => {
        void handleSubmit(e);
      }}
    >
      <select
        className="sv-obs-type-select"
        value={objectType}
        onChange={e => setObjectType(e.target.value as ObservationObjectType | '')}
        disabled={saving}
        aria-label="Tipo de elemento"
      >
        <option value="">— Tipo de elemento —</option>
        {(Object.entries(OBSERVATION_TYPE_LABELS) as [ObservationObjectType, string][]).map(
          ([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          )
        )}
      </select>
      <textarea
        className="sv-obs-textarea"
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Descripción de la observación…"
        rows={3}
        maxLength={500}
        disabled={saving}
      />
      {error && <p className="sv-obs-form-error">{error}</p>}
      <div className="sv-obs-form-actions">
        <button type="button" className="sv-obs-btn-cancel" onClick={onCancel} disabled={saving}>
          Cancelar
        </button>
        <button type="submit" className="sv-obs-btn-save" disabled={saving || !description.trim()}>
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}
